import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Scale,
  MessageCircle,
  GraduationCap,
  Award,
  Briefcase,
  ShieldCheck,
  BookOpen,
  ShoppingBag,
  Quote,
  ChevronRight,
  MapPin,
  Mail,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRACTICE_AREAS = [
  {
    icon: BookOpen,
    title: "Direito Civil",
    desc: "Contratos, família, sucessões, responsabilidade civil e cobranças.",
  },
  {
    icon: Briefcase,
    title: "Direito do Trabalho",
    desc: "Rescisões, verbas trabalhistas, assédio moral, equiparação salarial e ações na Justiça do Trabalho.",
  },
  {
    icon: ShieldCheck,
    title: "Direito Previdenciário",
    desc: "Aposentadorias, BPC/LOAS, auxílios do INSS, revisões e perícias previdenciárias.",
  },
  {
    icon: ShoppingBag,
    title: "Direito do Consumidor",
    desc: "Produtos com vício, cobrança indevida, negativação irregular, compras online e SAC.",
  },
];

const VALUES = [
  { title: "Atendimento humano", desc: "Linguagem clara, sem juridiquês desnecessário." },
  { title: "Estudo do caso", desc: "Cada cliente recebe análise personalizada do contexto." },
  { title: "Transparência", desc: "Honorários e prazos explicados antes de qualquer contratação." },
  { title: "Ética profissional", desc: "Atuação dentro do Código de Ética da OAB." },
];

export default function About() {
  const [config, setConfig] = useState({ whatsapp_link: "#", lawyer_name: "Dr. Edson Bispo" });

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border" data-testid="about-hero">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1623197532650-bacb8a68914e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMHdoaXRlJTIwbWFyYmxlJTIwdGV4dHVyZXxlbnwwfHx8fDE3Nzc0MTcyODh8MA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 paper-grain opacity-50" />
        <div className="absolute inset-0 bg-background/75" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-14 pb-16 md:pt-20 md:pb-24">
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-8 md:gap-12 items-center">
            {/* Portrait */}
            <div className="mx-auto md:mx-0">
              <div className="relative">
                <div className="absolute -inset-2 rounded-full bg-accent/20 blur-md" aria-hidden />
                <div
                  className="relative h-56 w-56 md:h-64 md:w-64 rounded-full border-2 border-accent/50 overflow-hidden shadow-md bg-secondary"
                  data-testid="lawyer-portrait"
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_dr-justice-hub/artifacts/er2mses8_IMG_20231023_091401_771.jpg"
                    alt="Dr. Edson Bispo"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center 18%" }}
                  />
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="text-center md:text-left">
              <p className="uppercase tracking-[0.28em] text-xs font-semibold text-accent mb-3 inline-flex items-center gap-2">
                <Scale className="h-3.5 w-3.5" /> Advogado responsável
              </p>
              <h1
                className="font-serif text-5xl md:text-6xl text-primary font-semibold tracking-tight leading-[1.05]"
                data-testid="lawyer-name"
              >
                Dr. Edson <span className="italic text-accent">Bispo</span>
              </h1>
              <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-xl">
                Advogado dedicado a orientar pessoas em momentos delicados — quando saber
                o próximo passo faz toda a diferença. Atuação em{" "}
                <strong className="text-primary">Direito Civil</strong>,{" "}
                <strong className="text-primary">Trabalhista</strong>,{" "}
                <strong className="text-primary">Previdenciário</strong> e{" "}
                <strong className="text-primary">do Consumidor</strong>.
              </p>

              <div className="mt-6 flex flex-wrap items-center justify-center md:justify-start gap-2">
                <Badge className="bg-primary text-primary-foreground rounded-sm tracking-wider text-[10px] uppercase">
                  OAB/SP 338.390
                </Badge>
                <Badge variant="outline" className="border-border text-muted-foreground rounded-sm tracking-wider text-[10px] uppercase">
                  Especialista • Pós-graduado
                </Badge>
              </div>

              <div className="mt-7 flex flex-wrap items-center justify-center md:justify-start gap-3">
                <a href={config.whatsapp_link} target="_blank" rel="noreferrer" data-testid="about-cta-whatsapp">
                  <Button
                    size="lg"
                    className="rounded-full bg-[#25D366] hover:bg-[#1faa55] text-white h-12 px-7 text-sm tracking-wide"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Falar agora no WhatsApp
                  </Button>
                </a>
                <Link to="/" data-testid="about-cta-home">
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full border-border bg-white/80 hover:bg-white h-12 px-7 text-sm tracking-wide"
                  >
                    Fazer uma pergunta
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE / PHILOSOPHY */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20 text-center" data-testid="about-philosophy">
        <Quote className="h-7 w-7 text-accent mx-auto mb-5" strokeWidth={1.5} />
        <p className="font-serif text-2xl md:text-3xl text-primary leading-snug italic">
          &ldquo;O Direito existe para proteger pessoas. Meu papel é traduzir a lei
          para a vida real do cliente — com clareza, ética e técnica.&rdquo;
        </p>
        <p className="mt-5 text-sm uppercase tracking-[0.22em] text-muted-foreground">
          — Dr. Edson Bispo
        </p>
      </section>

      {/* BIO */}
      <section className="bg-secondary/40 border-y border-border" data-testid="about-bio">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-2 gap-10">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-3">
              Trajetória
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-primary font-medium mb-5">
              Quem é o Dr. Edson Bispo
            </h2>
            <div className="legal-prose">
              <p>
                Advogado militante, com formação sólida e prática consolidada na orientação
                de pessoas físicas e famílias. <strong>Especialista e pós-graduado</strong> em
                Direito do Consumidor, Direito do Trabalho, Direito Previdenciário e Direito Civil —
                quatro áreas que, juntas, alcançam a maior parte das demandas reais dos brasileiros.
              </p>
              <p>
                Atua tanto em consultoria preventiva — analisando contratos, situações de risco
                e planejamento previdenciário — quanto em ações judiciais, quando o caminho
                amigável já se mostrou esgotado.
              </p>
              <p>
                Acredita que conhecimento jurídico não é privilégio: por isso atende com
                linguagem direta, escuta o caso de cada pessoa antes de propor solução, e
                explica todos os custos e prazos envolvidos.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Card className="bg-white border-border rounded-xl">
              <CardContent className="p-5 flex items-start gap-4">
                <div className="rounded-lg bg-secondary p-3">
                  <GraduationCap className="h-5 w-5 text-primary" strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-primary">Formação</h3>
                  <p className="text-sm text-muted-foreground">
                    Bacharel em Direito • <strong className="text-primary">Especialista e pós-graduado</strong> em
                    Direito do Consumidor, Direito do Trabalho, Direito Previdenciário e Direito Civil.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-border rounded-xl">
              <CardContent className="p-5 flex items-start gap-4">
                <div className="rounded-lg bg-secondary p-3">
                  <Award className="h-5 w-5 text-primary" strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-primary">Registro Profissional</h3>
                  <p className="text-sm text-muted-foreground">
                    Inscrito na <strong className="text-primary">OAB/SP sob o nº 338.390</strong>, em pleno
                    exercício da advocacia.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-border rounded-xl">
              <CardContent className="p-5 flex items-start gap-4">
                <div className="rounded-lg bg-secondary p-3">
                  <MapPin className="h-5 w-5 text-primary" strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-primary">Atendimento</h3>
                  <p className="text-sm text-muted-foreground">
                    Presencial em São Paulo • Online para todo o Brasil via WhatsApp e videochamada.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* PRACTICE AREAS */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="about-areas">
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-3">
          Áreas de atuação
        </p>
        <h2 className="font-serif text-3xl md:text-4xl text-primary font-medium mb-10">
          Em que o Dr. Edson pode te ajudar
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {PRACTICE_AREAS.map((a, i) => {
            const Icon = a.icon;
            return (
              <Card
                key={a.title}
                className="bg-white border-border rounded-xl hover:-translate-y-1 hover:border-accent transition-all"
                data-testid={`about-area-${i}`}
              >
                <CardContent className="p-6">
                  <div className="rounded-lg bg-secondary p-3 inline-block mb-4">
                    <Icon className="h-6 w-6 text-primary" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif text-2xl text-primary font-semibold leading-tight">
                    {a.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">{a.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-secondary/40 border-y border-border" data-testid="about-values">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-3">
            Princípios
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-primary font-medium mb-10">
            O que orienta a advocacia do Dr. Edson
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-7">
            {VALUES.map((v, i) => (
              <div key={v.title} className="flex gap-4">
                <div className="font-serif text-2xl text-accent leading-none pt-1">
                  0{i + 1}
                </div>
                <div>
                  <h3 className="font-serif text-xl text-primary mb-1">{v.title}</h3>
                  <p className="text-sm text-muted-foreground">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="about-cta-strip">
        <div className="rounded-2xl border border-border bg-white p-8 md:p-12 text-center">
          <Scale className="h-8 w-8 text-accent mx-auto mb-4" strokeWidth={1.4} />
          <h2 className="font-serif text-3xl md:text-4xl text-primary font-medium">
            Vamos conversar sobre o seu caso?
          </h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Conte sua situação pelo WhatsApp. O atendimento inicial é direto com o Dr. Edson Bispo
            — sem intermediários e sem compromisso.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <a href={config.whatsapp_link} target="_blank" rel="noreferrer" data-testid="about-cta-whatsapp-bottom">
              <Button
                size="lg"
                className="rounded-full bg-[#25D366] hover:bg-[#1faa55] text-white h-12 px-7"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                Falar com Dr. Edson Bispo
              </Button>
            </a>
            <a href="mailto:bisposuoz@hotmail.com" data-testid="about-cta-email">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-border bg-white hover:bg-secondary h-12 px-7"
              >
                <Mail className="mr-2 h-4 w-4" />
                bisposuoz@hotmail.com
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 text-sm text-muted-foreground flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Scale className="h-5 w-5 text-accent" />
            <span className="font-serif text-lg text-primary">Pergunte ao Dr.</span>
          </div>
          <p className="text-xs max-w-md md:text-right">
            Conteúdo informativo. Não substitui contratação de advogado para análise do caso concreto.
          </p>
        </div>
      </footer>
    </div>
  );
}
